// src/components/admin/act/report/ArchivedACTStudentReportViewer.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { db } from "../../../../firebaseConfig";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  writeBatch,
} from "firebase/firestore";
import { useReactToPrint } from "react-to-print";
import { BiArchiveIn } from "react-icons/bi";
import { RiErrorWarningFill } from "react-icons/ri";
import { FaCheckCircle } from "react-icons/fa";

// Sub-components
import ACTDetailedQuestionReport from "./ACTDetailedQuestionReport";
import ACTPerformanceReport from "./ACTPerformanceReport";
import ACTSubcategoryBreakdown from "./ACTSubcategoryBreakdown";
import ACTSearchableStudentDropdown from "./ACTSearchableStudentDropdown";

// ---------------- helpers ----------------
function toDateSafe(v) {
  if (!v) return null;
  if (typeof v?.toDate === "function") return v.toDate(); // Firestore Timestamp
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateShort(v) {
  const d = toDateSafe(v);
  if (!d) return "—";
  return d.toLocaleDateString();
}

function buildAttemptLabel(attempt) {
  const title =
    attempt?.testTitle ||
    attempt?.testName ||
    attempt?.displayName ||
    (attempt?.testId ? `ACT Diagnostic (${attempt.testId})` : "ACT Diagnostic");

  const completed = attempt?.completedAt || attempt?.completed_on || attempt?.completedAtIso;
  const dateLabel = completed ? formatDateShort(completed) : "—";

  const score =
    attempt?.compositeScore ??
    attempt?.composite ??
    attempt?.score ??
    null;

  const scoreLabel = score == null ? "" : ` — Composite ${score}`;

  return `${title} — Completed ${dateLabel}${scoreLabel}`;
}

async function fetchAttemptsForStudent(studentId) {
  const results = [];

  // New Standard: diagnostic_results where studentId matches, type === ACT
  try {
    const q = query(collection(db, "diagnostic_results"), where("studentId", "==", studentId));
    const snap = await getDocs(q);
    snap.forEach((d) => {
      const data = d.data() || {};
      if (data.type !== "ACT") return;
      if (!data.isArchived) return; // <--- ADD THIS LINE (Skip if NOT archived)

      // only completed
      if (data.status === "completed" || !!data.completedAt) {
        results.push({ attemptId: d.id, ...data });
      }
    });
  } catch (e) {
    console.warn("Error fetching diagnostic_results collection:", e);
  }

  return results;
}

// ---------------- styles (same feel as SAT viewer) ----------------
const styles = {
  page: {
    minHeight: "100vh",
    padding: "24px 16px",
    background:
      "radial-gradient(circle at top, #e0f2fe 0, #eef2ff 40%, #f9fafb 100%)",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
  },
  container: { maxWidth: 1100, margin: "0 auto" },

  topCard: {
    borderRadius: 18,
    background: "rgba(255,255,255,0.78)",
    border: "1px solid rgba(229,231,235,0.9)",
    boxShadow: "0 18px 45px rgba(15, 23, 42, 0.08)",
    backdropFilter: "blur(8px)",
    overflow: "visible",
  },

  header: {
    padding: "18px 18px 10px",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  titleBlock: { display: "flex", flexDirection: "column", gap: 4 },
  title: {
    margin: 0,
    fontSize: 20,
    fontWeight: 800,
    color: "#0f172a",
    letterSpacing: "-0.01em",
  },
  subtitle: { margin: 0, fontSize: 13, color: "#64748b", lineHeight: 1.35 },
  badge: {
    fontSize: 11,
    padding: "6px 10px",
    borderRadius: 999,
    background: "linear-gradient(90deg, #dbeafe, #e0e7ff)",
    color: "#1d4ed8",
    border: "1px solid #bfdbfe",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    whiteSpace: "nowrap",
  },

  controls: {
    padding: "12px 18px 16px",
    borderTop: "1px solid rgba(226,232,240,0.8)",
    display: "grid",
    gridTemplateColumns: "1.2fr 1.8fr",
    gap: 12,
    alignItems: "start",
  },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: {
    fontSize: 12,
    color: "#334155",
    fontWeight: 800,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
  select: {
    width: "100%",
    fontSize: 14,
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid #d1d5db",
    backgroundColor: "#ffffff",
    outline: "none",
    boxShadow: "0 8px 20px rgba(15,23,42,0.06)",
    boxSizing: "border-box",
    height: 46,
    minHeight: 44,
  },
  hint: { fontSize: 12, color: "#64748b", marginTop: 2 },

  reportShell: {
    marginTop: 20,
    borderRadius: 18,
    backgroundColor: "#ffffff",
    border: "1px solid #e5e7eb",
    boxShadow: "0 16px 40px rgba(15, 23, 42, 0.06)",
    overflow: "hidden",
  },
  // Update reportHeader to align items correctly
  reportHeader: {
    padding: "20px 24px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    backgroundColor: "#ffffff",
  },
  
  // New Styles for Header Data
  studentName: { margin: 0, fontSize: 20, fontWeight: 900, color: "#000" },
  studentEmail: { fontSize: 13, color: "#64748b", marginTop: 4 },
  attemptIdRow: { fontSize: 12, color: "#94a3b8", marginTop: 4, fontFamily: "monospace" },
  
  headerRight: { textAlign: "right" },
  completedLabel: { fontSize: 10, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" },
  completedDate: { fontSize: 12, fontWeight: 900, color: "#0f172a", marginTop: 2 },

  // New Button Row (between sections)
  actionBar: {
    display: "flex",
    gap: 12,
    marginTop: 30,
    marginBottom: 20,
    alignItems: "center",
  },
  actionBtn: {
    padding: "10px 16px",
    borderRadius: 8,
    border: "none",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: "#fff",
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
  },

  // Archive Button (Red style for top control bar)
  archiveBtn: {
    padding: "0 16px",
    height: 46,
    borderRadius: 12,
    border: "1px solid #fecaca",
    background: "#fef2f2",
    color: "#dc2626",
    fontWeight: 700,
    fontSize: 13,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 6,
    whiteSpace: "nowrap",
  },

  headerActions: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },
  btn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    background: "#ffffff",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 800,
    color: "#0f172a",
    boxShadow: "0 10px 18px rgba(15,23,42,0.08)",
  },

  reportBody: { padding: 18 },
  loading: { padding: 12, color: "#6b7280" },
  error: {
    color: "#b91c1c",
    backgroundColor: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: 10,
    padding: "10px 12px",
    fontSize: 13,
    marginTop: 12,
  },
};

const printPageStyle = `
  @page { size: auto; margin: 12mm; }
  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
`;

export default function ACTStudentReportViewer() {
  const [searchParams] = useSearchParams();
  const urlStudentId = String(searchParams.get("studentId") || "").trim();
  const urlAttemptId = String(searchParams.get("attemptId") || "").trim();

  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [studentsError, setStudentsError] = useState("");

  const [selectedStudentId, setSelectedStudentId] = useState(urlStudentId);
  const [selectedStudentInfo, setSelectedStudentInfo] = useState(null);

  const [attempts, setAttempts] = useState([]);
  const [attemptsLoading, setAttemptsLoading] = useState(false);

  const [selectedAttemptId, setSelectedAttemptId] = useState("");
  const [selectedAttempt, setSelectedAttempt] = useState(null);
  const [showQuestions, setShowQuestions] = useState(false);
  const [showIntegrityTooltip, setShowIntegrityTooltip] = useState(false);

  const reportRef = useRef(null);

  // Print
  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: "ACT Diagnostic Report",
    pageStyle: printPageStyle,
  });

  // ------------------------------------------------
  // 1) Load Students from ACT diagnostic_results
  // ------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    async function loadStudentsFromDiagnostics() {
      setStudentsLoading(true);
      setStudentsError("");

      try {
        const diagRef = collection(db, "diagnostic_results");
        const snapshot = await getDocs(diagRef);

        // Map: studentId -> mostRecentCompletedAtMillis
        const studentMap = new Map();

        snapshot.forEach((docSnap) => {
          const d = docSnap.data() || {};
          if (d.type !== "ACT") return;
          if (!d.isArchived) return; // Only allow archived items

          const sid = d.studentId;
          if (!sid) return;

          const isCompleted = d.status === "completed" || !!d.completedAt;
          if (!isCompleted) return;

          const dateObj = toDateSafe(d.completedAt || d.completed_on || d.completedAtIso);
          const timeVal = dateObj ? dateObj.getTime() : 0;

          if (!studentMap.has(sid)) studentMap.set(sid, timeVal);
          else if (timeVal > studentMap.get(sid)) studentMap.set(sid, timeVal);
        });

        const uniqueIds = Array.from(studentMap.keys());

        if (uniqueIds.length === 0) {
          if (!cancelled) setStudents([]);
          return;
        }

        const userPromises = uniqueIds.map(async (uid) => {
          try {
            const userSnap = await getDoc(doc(db, "users", uid));
            const userData = userSnap.exists() ? userSnap.data() : {};
            const lastMs = studentMap.get(uid) || 0;
            return {
              uid,
              name: userData.name || "Unknown Name",
              email: userData.email || "",
              lastCompletedAt: lastMs,
              lastDateLabel: lastMs > 0 ? new Date(lastMs).toLocaleDateString() : "N/A",
            };
          } catch (err) {
            console.error("Error fetching user", uid, err);
            const lastMs = studentMap.get(uid) || 0;
            return { uid, name: "Unknown (Error)", email: "", lastCompletedAt: lastMs, lastDateLabel: "N/A" };
          }
        });

        const loadedStudents = await Promise.all(userPromises);
        loadedStudents.sort((a, b) => (b.lastCompletedAt || 0) - (a.lastCompletedAt || 0));

        if (!cancelled) setStudents(loadedStudents);
      } catch (err) {
        console.error(err);
        if (!cancelled) setStudentsError("Failed to load students from ACT diagnostics.");
      } finally {
        if (!cancelled) setStudentsLoading(false);
      }
    }

    loadStudentsFromDiagnostics();
    return () => {
      cancelled = true;
    };
  }, []);

  // ------------------------------------------------
  // 2) When student changes, load ACT attempts
  // ------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    async function loadAttempts() {
      setAttempts([]);
      setSelectedAttemptId("");
      setSelectedAttempt(null);

      if (!selectedStudentId) return;

      setAttemptsLoading(true);

      try {
        const atts = await fetchAttemptsForStudent(selectedStudentId);
        atts.sort((a, b) => {
          const ta = toDateSafe(a.completedAt || a.completed_on || a.completedAtIso)?.getTime() || 0;
          const tb = toDateSafe(b.completedAt || b.completed_on || b.completedAtIso)?.getTime() || 0;
          return tb - ta;
        });

        if (!cancelled) setAttempts(atts);

        // Prefer the deep-linked attempt, else auto-select newest
        if (!cancelled && atts.length > 0) {
          const preferred =
            (urlAttemptId && atts.find((a) => a.attemptId === urlAttemptId)) || atts[0];
          setSelectedAttemptId(preferred.attemptId);
          setSelectedAttempt(preferred);
        }
      } catch (err) {
        console.error(err);
      } finally {
        if (!cancelled) setAttemptsLoading(false);
      }
    }

    loadAttempts();
    return () => {
      cancelled = true;
    };
  }, [selectedStudentId, urlAttemptId]);

  // Update selected attempt from dropdown
  useEffect(() => {
    if (!selectedAttemptId) return;
    const found = attempts.find((a) => a.attemptId === selectedAttemptId);
    setSelectedAttempt(found || null);
  }, [selectedAttemptId, attempts]);

  // selected student info (for header)
  useEffect(() => {
    const info = students.find((s) => s.uid === selectedStudentId);
    setSelectedStudentInfo(info || null);
  }, [selectedStudentId, students]);

  const attemptLabelMap = useMemo(() => {
    const m = new Map();
    attempts.forEach((a) => m.set(a.attemptId, buildAttemptLabel(a)));
    return m;
  }, [attempts]);

  const restoreStudent = async () => { 
  if (!selectedStudentId) return;
  try {
    const batch = writeBatch(db);
    const q = query(collection(db, "diagnostic_results"), where("studentId", "==", selectedStudentId));
    const snap = await getDocs(q);

    snap.forEach((docSnap) => {
      const d = docSnap.data() || {};
      if (d.type !== "ACT") return;
      // Ensure this sets isArchived to false
      batch.update(docSnap.ref, { isArchived: false });
    });

    await batch.commit();

    setStudents((prev) => prev.filter((s) => s.uid !== selectedStudentId));
    setSelectedStudentId("");
    setSelectedAttemptId("");
    setSelectedAttempt(null);

    // Update the alert text
    alert("Student restored from ACT Archive."); 
  } catch (e) {
    console.error(e);
    alert("Error restoring student.");
  }
};

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.topCard}>
          <div style={styles.header}>
            <div style={styles.titleBlock}>
              <h1 style={styles.title}>ACT Diagnostic Report</h1>
              <p style={styles.subtitle}>
                View Composite + section scores, Rx Hours, and question-level breakdown (grouped by subject).
              </p>
            </div>
            <div style={styles.badge}>Admin</div>
          </div>

          <div style={styles.controls}>
            {/* Left: Student Search */}
            <div style={styles.field}>
              <div style={styles.label}>Student</div>
              <ACTSearchableStudentDropdown
                students={students}
                loading={studentsLoading}
                error={studentsError}
                selectedId={selectedStudentId}
                onSelect={(uid) => setSelectedStudentId(uid)}
                placeholder={studentsLoading ? "Loading…" : "Search student…"}
              />
              <div style={styles.hint}>
                {studentsLoading ? "Loading students…" : `Showing ${students.length} students.`}
              </div>
            </div>

            {/* Right: Attempt Select + Archive Button */}
            <div style={styles.field}>
              <div style={styles.label}>Attempt / Test</div>
              <div style={{ display: "flex", gap: 10 }}>
                <select
                  style={{ ...styles.select, flex: 1 }}
                  disabled={!selectedStudentId || attemptsLoading || attempts.length === 0}
                  value={selectedAttemptId}
                  onChange={(e) => setSelectedAttemptId(e.target.value)}
                >
                  <option value="">{attemptsLoading ? "Loading…" : "Select an attempt"}</option>
                  {attempts.map((a) => (
                    <option key={a.attemptId} value={a.attemptId}>
                      {attemptLabelMap.get(a.attemptId)}
                    </option>
                  ))}
                </select>

                {/* Archive Button Moved Here */}
                <button 
                  style={{ ...styles.archiveBtn, borderColor: "#bbf7d0", background: "#f0fdf4", color: "#16a34a" }} 
                  onClick={restoreStudent} 
                  disabled={!selectedStudentId}
                  title="Restore this student"
                >
                  <BiArchiveIn size={16} style={{ transform: "rotate(180deg)" }} /> Restore
                </button>
              </div>
              
              <div style={styles.hint}>
                {attemptsLoading
                  ? "Loading attempts…"
                  : attempts.length === 0
                  ? "No completed ACT attempts found."
                  : "Newest attempt auto-selected."}
              </div>
            </div>
          </div>
        </div>

        {selectedStudentId && selectedAttempt && (
          <div style={styles.reportShell}>
            
            {/* NEW HEADER LAYOUT */}
            <div style={styles.reportHeader}>
              <div>
                <h2 style={styles.studentName}>{selectedStudentInfo?.name || "Student"}</h2>
                <div style={styles.studentEmail}>{selectedStudentInfo?.email || ""}</div>
                <div style={styles.attemptIdRow}>
                   ACT Diagnostic • Attempt ID: {selectedAttempt.attemptId}
                </div>
              </div>

              <div style={styles.headerRight}>
                <div style={styles.completedLabel}>COMPLETED ON</div>
                <div style={styles.completedDate}>
                   {formatDateShort(selectedAttempt.completedAt || selectedAttempt.completed_on || selectedAttempt.completedAtIso)}
                   {selectedAttempt.completedAt && typeof selectedAttempt.completedAt.toDate === 'function' &&
                      ` at ${selectedAttempt.completedAt.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                   }
                </div>

                {/* Integrity Flag */}
                {selectedAttempt?.integrityLog && (
                  <div 
                    style={{ 
                      display: "flex", alignItems: "center", justifyContent: "flex-end", 
                      gap: 6, marginTop: 8, position: "relative", cursor: "help" 
                    }}
                    onMouseEnter={() => setShowIntegrityTooltip(true)}
                    onMouseLeave={() => setShowIntegrityTooltip(false)}
                  >
                    <span style={{ 
                      fontSize: 12, fontWeight: 700, 
                      color: selectedAttempt.integrityLog.cheated_triggered ? "#b91c1c" : "#15803d" 
                    }}>
                      {selectedAttempt.integrityLog.cheated_triggered ? "Integrity Flagged" : "Integrity Verified"}
                    </span>
                    
                    {selectedAttempt.integrityLog.cheated_triggered ? (
                      <RiErrorWarningFill size={18} color="#dc2626" />
                    ) : (
                      <FaCheckCircle size={16} color="#16a34a" />
                    )}

                    {/* Hover Tooltip */}
                    {showIntegrityTooltip && (
                      <div style={{
                        position: "absolute", top: "100%", right: 0, marginTop: 8, width: 200,
                        backgroundColor: "#1e293b", color: "#f8fafc", padding: "12px", 
                        borderRadius: 8, fontSize: 12, textAlign: "left", zIndex: 50, 
                        boxShadow: "0 10px 25px rgba(0,0,0,0.2)"
                      }}>
                        <div style={{ fontWeight: 800, marginBottom: 8, borderBottom: "1px solid #334155", paddingBottom: 4 }}>
                          Integrity Log Details
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                          <span>Tab Switches:</span> 
                          <strong>{selectedAttempt.integrityLog.tabSwitchCount}</strong>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                          <span>Fullscreen Exits:</span> 
                          <strong>{selectedAttempt.integrityLog.fullscreenExitCount}</strong>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span>Inactive Time:</span> 
                          <strong>{selectedAttempt.integrityLog.totalInactiveTime} sec</strong>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* REPORT BODY */}
            <div style={styles.reportBody} ref={reportRef}>
              <ACTPerformanceReport
                studentId={selectedStudentId}
                testId={selectedAttempt.testId}
                summary={selectedAttempt}
              />

              {/* Added margin wrapper here */}
              <div style={{ marginTop: 20 }}>
                <ACTSubcategoryBreakdown
                  studentId={selectedStudentId}
                  testId={selectedAttempt.testId}
                  attemptId={selectedAttempt.attemptId}
                />
              </div>

              {/* ACTION BAR: Toggle Questions & Print */}
              <div style={styles.actionBar} data-html2canvas-ignore="true">
                <button
                  style={{ ...styles.actionBtn, background: "#111827" }}
                  onClick={() => setShowQuestions(!showQuestions)}
                >
                  {showQuestions ? "Hide Question Breakdown" : "Show Question Breakdown"}
                </button>

                <button
                  style={{ ...styles.actionBtn, background: "#2563eb" }}
                  onClick={handlePrint}
                >
                  Print / Save as PDF
                </button>
              </div>

              {/* CONDITIONAL QUESTION BREAKDOWN */}
              {showQuestions && (
                <div style={{ marginTop: 20 }}>
                  <ACTDetailedQuestionReport
                    studentId={selectedStudentId}
                    testId={selectedAttempt.testId}
                    attemptId={selectedAttempt.attemptId}
                  />
                  
                  {/* Close Button at bottom for convenience */}
                  <div style={{ marginTop: 20, textAlign: "center" }}>
                    <button 
                       style={{ ...styles.actionBtn, background: "#fff", color: "#374151", border: "1px solid #d1d5db", margin: "0 auto" }}
                       onClick={() => {
                         setShowQuestions(false);
                         window.scrollTo({ top: 0, behavior: "smooth" });
                       }}
                    >
                      Close & Scroll Top
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {!selectedStudentId && !studentsLoading && (
          <div style={styles.loading}>Select a student to view their ACT report.</div>
        )}
      </div>
    </div>
  );
}
